import React, { useState } from "react";
import moment from "moment";
import { WhmDatePicker, WhmTextField } from "@securitize/reactjs-whm";
import { useFundHoldingsDateRangeQuery } from "../../data/fundHoldingsRequest";
import useScreenSize from "../../hooks/useScreenSize";

const DatePicker = WhmDatePicker<moment.Moment, moment.Moment>;

type Props = {
  tokenId: string;
  value: string | null | undefined;
  onDateChange: (date: string) => void;
};

export function FundHoldingsSourceDatePicker({
  tokenId,
  value,
  onDateChange,
}: Props) {
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const query = useFundHoldingsDateRangeQuery({
    tokenId,
  });
  const dates = query.data?.dates ?? [];

  if (dates.length === 0)
    return <FundHoldingsDummyDatePicker onHover={() => {}} />;

  const datesMap = new Map(
    // Keep the original date string as value to ensure exact match
    dates?.map((date) => [moment(date).utc().format("YYYY-MM-DD"), date]),
  );
  return (
    <DatePicker
      label={"Select Reporting Date"}
      disableFuture
      minDate={moment(dates[0]).utc()}
      maxDate={moment(dates[dates.length - 1]).utc()}
      value={moment(value ?? dates[dates.length - 1]).utc()}
      shouldDisableDate={(date) => {
        return !datesMap.has(date.format("YYYY-MM-DD"));
      }}
      onChange={(newDate: moment.Moment | null) => {
        const date = datesMap.get(newDate?.format("YYYY-MM-DD") ?? "");
        if (newDate?.isValid() && date) {
          onDateChange(date);
        }
      }}
      open={openPicker}
      onOpen={() => setOpenPicker(true)}
      onClose={() => setOpenPicker(false)}
      renderInput={(props) => (
        <WhmTextField
          {...props}
          sx={{ width: "100%" }}
          size="small"
          disabled={query.isLoading || query.isError}
        />
      )}
      disabled={query.isLoading || query.isError}
    />
  );
}

export function FundHoldingsDummyDatePicker({
  onHover,
}: {
  onHover: () => void;
}) {
  return (
    <DatePicker
      label={"Select Reporting Date"}
      disableFuture
      value={moment()}
      onChange={() => {}}
      open={false}
      onOpen={() => {}}
      onClose={() => {}}
      renderInput={(props) => (
        <WhmTextField
          {...props}
          sx={{ width: "100%" }}
          size="small"
          onMouseEnter={onHover}
        />
      )}
    />
  );
}

export default function FundHoldingsDatePicker({
  tokenId,
  value,
  onDateChange,
}: Props) {
  const { isSmall, isMedium } = useScreenSize();
  const [hovered, setHovered] = useState(isSmall || isMedium);
  return hovered ? (
    <FundHoldingsSourceDatePicker
      tokenId={tokenId}
      value={value}
      onDateChange={onDateChange}
    />
  ) : (
    <FundHoldingsDummyDatePicker onHover={() => setHovered(true)} />
  );
}
